import type { AxiosInstance, AxiosResponse } from 'axios'
import apiClient from '@/api/client'

export interface PaginatedResponse<T> {
  items: T[]
  total: number
  page: number
  page_size: number
  total_pages: number
}

export interface QueryParams {
  page?: number
  page_size?: number
  search?: string
  sort_by?: string
  sort_order?: 'asc' | 'desc'
  [key: string]: unknown
}

export class BaseApiService<T> {
  protected client: AxiosInstance
  protected endpoint: string

  constructor(endpoint: string) {
    this.client = apiClient
    this.endpoint = endpoint
  }

  /** Endpoint with trailing slash — avoids FastAPI 307 redirects behind ALB. */
  protected get collectionUrl(): string {
    return this.endpoint.endsWith('/') ? this.endpoint : `${this.endpoint}/`
  }

  async getAll(params?: QueryParams): Promise<AxiosResponse<PaginatedResponse<T>>> {
    return this.client.get(this.collectionUrl, { params })
  }

  async getById(id: number | string): Promise<AxiosResponse<T>> {
    return this.client.get(`${this.endpoint}/${id}`)
  }

  async create(data: Partial<T>): Promise<AxiosResponse<T>> {
    return this.client.post(this.collectionUrl, data)
  }

  async update(id: number | string, data: Partial<T>): Promise<AxiosResponse<T>> {
    return this.client.put(`${this.endpoint}/${id}`, data)
  }

  async patch(id: number | string, data: Partial<T>): Promise<AxiosResponse<T>> {
    return this.client.patch(`${this.endpoint}/${id}`, data)
  }

  async delete(id: number | string): Promise<AxiosResponse<void>> {
    return this.client.delete(`${this.endpoint}/${id}`)
  }
}
